import type { User } from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';

import { userProfileSchema, type UserProfile } from '@/core/schemas';
import { toAppError } from '@/infrastructure/errors/AppError';
import { firestore } from '@/infrastructure/firebase/app';
import { createConverter } from '@/infrastructure/firestore/converter';
import { firestorePaths } from '@/infrastructure/firestore/paths';

const converter = createConverter(userProfileSchema, 'users', (documentId) => ({
  uid: documentId,
}));

/**
 * Firebase's `User` object carries the identity; `users/{uid}` is a mirror kept
 * for display and traceability. Deliberately no role field — see docs/SECURITY.md.
 */
export async function upsertUserProfile(user: User): Promise<void> {
  const reference = doc(firestore, firestorePaths.users, user.uid);

  try {
    const existing = await getDoc(reference);

    const identity = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoUrl: user.photoURL,
      lastLoginAt: serverTimestamp(),
    };

    if (existing.exists()) {
      // `createdAt` is intentionally untouched — the rules reject changing it.
      await setDoc(reference, identity, { merge: true });
      return;
    }

    await setDoc(reference, { ...identity, createdAt: serverTimestamp() });
  } catch (error) {
    throw toAppError(error);
  }
}

export async function fetchUserProfile(uid: string): Promise<UserProfile | null> {
  try {
    const snapshot = await getDoc(
      doc(firestore, firestorePaths.users, uid).withConverter(converter),
    );
    return snapshot.exists() ? snapshot.data() : null;
  } catch (error) {
    throw toAppError(error);
  }
}
